<script setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ChecklistEditor from '@/components/admin/report-qc/ChecklistEditor.vue'
import EngineSettings from '@/components/admin/report-qc/EngineSettings.vue'
import FeedbackList from '@/components/admin/report-qc/FeedbackList.vue'
import GeneralInstructions from '@/components/admin/report-qc/GeneralInstructions.vue'
import UsageReport from '@/components/admin/report-qc/UsageReport.vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

/**
 * RED-361 / RED-384: Admin → Aruande kontroll. Five parts on a left sub-nav,
 * chosen by `?part=` so a link can open any of them. `seaded` (RED-384),
 * `uldjuhis` (RED-385), `kontrollnimekiri` (RED-386), `tagasiside` (RED-389)
 * and `kasutus` (RED-390, what the checks cost).
 */
const props = defineProps({
  part: { type: String, required: true },
  settings: { type: Object, required: true },
  bounds: { type: Object, required: true },
  versions: { type: Array, default: undefined },
  checklist: { type: Object, required: true },
  checklistVersions: { type: Array, default: undefined },
  // RED-387: the newest trial on the invented report and the current version's range.
  trial: { type: Object, required: true },
  // RED-389: feedback since the admin last opened Tagasiside (0 while on it),
  // and the list itself, sent only on that part.
  feedbackNew: { type: Number, default: 0 },
  feedback: { type: Object, default: null },
  // RED-390: usage and cost, sent only on that part.
  usage: { type: Object, default: null },
})

const { t } = useI18n()

// Items, questions and Üldjuhis share one version counter (design-spec §1).
const checklistMeta = computed(() => (props.checklist.versionNumber ? `v${props.checklist.versionNumber}` : null))

const parts = computed(() => [
  { key: 'kontrollnimekiri', meta: checklistMeta.value },
  { key: 'uldjuhis', meta: checklistMeta.value },
  { key: 'seaded', meta: props.settings.versionNumber ? `v${props.settings.versionNumber}` : null },
  {
    key: 'tagasiside',
    meta: props.feedbackNew > 0 ? t('messages.admin.report_qc.feedback.newBadge', { n: props.feedbackNew }) : null,
    isNew: props.feedbackNew > 0,
  },
  { key: 'kasutus', meta: null },
])
</script>

<template>
  <div class="py-6" data-testid="report-qc-admin">
    <div class="mb-6">
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="text-xl font-medium">{{ t('messages.admin.report_qc.title') }}</h1>
        <Tag :value="t('messages.admin.report_qc.beta')" severity="info" />
      </div>
      <p class="mt-1 text-sm text-gray-600">{{ t('messages.admin.report_qc.hint') }}</p>
    </div>

    <div class="flex flex-col gap-6 md:flex-row">
      <nav class="md:w-56 md:shrink-0" :aria-label="t('messages.admin.report_qc.tab')">
        <ul class="flex flex-col gap-1">
          <li v-for="item in parts" :key="item.key">
            <Link
              :href="route('admin.report-qc.index', { part: item.key })"
              preserve-scroll
              class="flex items-center justify-between rounded-md px-3 py-2 text-sm"
              :class="item.key === part ? 'bg-primary-50 font-medium text-primary-700' : 'text-gray-700 hover:bg-gray-100'"
              :aria-current="item.key === part ? 'page' : undefined"
              :data-testid="`report-qc-nav-${item.key}`"
            >
              <span>{{ t(`messages.admin.report_qc.nav.${item.key}`) }}</span>
              <span
                v-if="item.isNew"
                v-tooltip.top="t('messages.admin.report_qc.feedback.newSince')"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700"
                :data-testid="`report-qc-nav-${item.key}-new`"
              ><span class="inline-block size-2 rounded-full bg-primary-500" aria-hidden="true"></span>{{ item.meta }}</span>
              <span v-else-if="item.meta" class="font-mono text-xs text-gray-500">{{ item.meta }}</span>
            </Link>
          </li>
        </ul>
      </nav>

      <section class="min-w-0 flex-1">
        <EngineSettings v-if="part === 'seaded'" :settings="settings" :bounds="bounds" :versions="versions" />
        <GeneralInstructions v-else-if="part === 'uldjuhis'" :checklist="checklist" :versions="checklistVersions" :trial="trial" />
        <ChecklistEditor v-else-if="part === 'kontrollnimekiri'" :checklist="checklist" :versions="checklistVersions" :trial="trial" />
        <FeedbackList v-else-if="part === 'tagasiside' && feedback" :feedback="feedback" />
        <UsageReport v-else-if="part === 'kasutus' && usage" :usage="usage" />
      </section>
    </div>
  </div>
</template>
